import { useEffect, useState } from 'react';
import { apiGet } from './api';
import { TASKS_CHANGED_EVENT } from './taskEvents';
import { HISTORY_CHANGED_EVENT } from './history';

/**
 * Task status for task links in notes (#324). Links to the same task share one
 * request, and the cache empties whenever a task is written or an action is
 * undone, so links already on screen follow the change.
 */

const cache = new Map<string, Promise<string | null>>();

export function fetchTaskStatus(id: string): Promise<string | null> {
  if (!id) return Promise.resolve(null);
  let p = cache.get(id);
  if (!p) {
    p = apiGet<{ status?: unknown }>(`/tasks/${encodeURIComponent(id)}`)
      .then((t) => (typeof t?.status === 'string' ? t.status : null))
      .catch(() => null);
    cache.set(id, p);
  }
  return p;
}

export function resetTaskStatusCacheForTest(): void {
  cache.clear();
}

const listeners = new Set<() => void>();

function invalidate() {
  cache.clear();
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') {
  window.addEventListener(TASKS_CHANGED_EVENT, invalidate);
  window.addEventListener(HISTORY_CHANGED_EVENT, invalidate);
}

/** The task's status, or null while loading and when it cannot be read. */
export function useTaskStatus(id: string | null | undefined): string | null {
  const [status, setStatus] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const bump = () => setGeneration((g) => g + 1);
    listeners.add(bump);
    return () => { listeners.delete(bump); };
  }, []);

  useEffect(() => {
    if (!id) { setStatus(null); return; }
    let live = true;
    fetchTaskStatus(id).then((s) => { if (live) setStatus(s); });
    return () => { live = false; };
  }, [id, generation]);

  return status;
}
